import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { BookOpen, ArrowRight } from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * V232.4 — Case Study cross-link component for comparator pages.
 *
 * Pulls the most-recent Jendrál-generated blog post whose target
 * keyword / tags match the current competitor and renders it as a
 * "real case study" callout mid-page. This creates the internal
 * link flow:
 *     /compare/toprankpulse-vs-X  →  /blog/jendral-X-...  →  /snapshot
 *
 * Why this matters:
 *   • Time-on-site = AI crawler quality signal (dwell + path depth)
 *   • Jendrál articles have 85+ SEO score & FAQ triggers, so they
 *     rank for long-tail queries nobody else owns
 *   • Reader goes competitive-page → emotional-case-study → checkout
 *
 * `competitorKey` is a loose keyword that we fuzzy-match against the
 * post's `target_keyword` and `tags`. Returns `null` (renders nothing)
 * when no matching jendral post exists yet, so comparator pages still
 * render perfectly when the content pipeline is empty.
 */
export default function CaseStudyLink({ competitorKey }) {
  const [post, setPost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // The jendral list endpoint is admin-gated, so we use the public
    // blog search instead. Filter by category=Porovnania + language=sk.
    axios
      .get(`${API}/blog/posts?language=sk&category=Porovnania&limit=20`)
      .then((r) => {
        if (cancelled) return;
        const posts = r.data?.posts || r.data || [];
        // Fuzzy match: prefer the newest post whose target_keyword or
        // tags/title mentions the competitor (case-insensitive).
        const needle = (competitorKey || "").toLowerCase();
        const match = posts.find((p) => {
          const hay = [
            p.target_keyword || "",
            (p.tags || []).join(" "),
            p.title || "",
            p.slug || "",
          ].join(" ").toLowerCase();
          return hay.includes(needle);
        });
        // Fallback: just use the newest "Porovnania" post if no specific match.
        setPost(match || posts[0] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [competitorKey]);

  if (!post) return null;

  return (
    <section className="px-6 py-6 max-w-4xl mx-auto" data-testid={`case-study-link-${competitorKey}`}>
      <a
        href={`/blog/${post.slug}`}
        onClick={() => trackEvent("case_study_click", {
          competitor: competitorKey,
          slug: post.slug,
          source: "comparator_page",
        })}
        className="group block rounded-2xl border border-[#FFE600]/30 bg-gradient-to-r from-[#FFE600]/5 via-[#FFE600]/[0.02] to-transparent p-5 sm:p-6 hover:border-[#FFE600]/60 transition-colors"
        data-testid="case-study-anchor"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-[#FFE600]/10 border border-[#FFE600]/30 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-[#FFE600]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFE600] font-['JetBrains_Mono'] mb-1">
              Reálna case study · náš test
            </div>
            <h3 className="text-xl sm:text-2xl font-black font-['Cabinet_Grotesk'] text-white leading-tight mb-2 group-hover:text-[#FFE600] transition-colors" data-testid="case-study-title">
              {post.title}
            </h3>
            {post.excerpt && (
              <p className="text-sm text-gray-400 leading-relaxed line-clamp-2" data-testid="case-study-excerpt">
                {post.excerpt}
              </p>
            )}
            <div className="flex items-center gap-2 mt-3 text-sm text-[#a855f7] font-bold">
              Prečítať celú case study <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </a>
    </section>
  );
}
